import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, AuthGuard } from "@/components/AuthProvider";
import ErrorBoundary from "@/components/ErrorBoundary";
import DashboardLayout from "@/components/DashboardLayout";
import { Loader2 } from "lucide-react";

// Eager-loaded pages (auth flow — must load immediately)
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";

// Lazy-loaded pages (code-split per route)
const Index = lazy(() => import("./pages/Index"));
const ShiftsPage = lazy(() => import("./pages/ShiftsPage"));
const CustomersPage = lazy(() => import("./pages/CustomersPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const BookingUrlsPage = lazy(() => import("./pages/BookingUrlsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const ShopsPage = lazy(() => import("./pages/ShopsPage"));
const StaffPage = lazy(() => import("./pages/StaffPage"));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage"));
const PublicBookingPage = lazy(() => import("./pages/PublicBookingPage"));
const PublicBookingCheckPage = lazy(() => import("./pages/PublicBookingCheckPage"));
const PublicMenuListPage = lazy(() => import("./pages/PublicMenuListPage"));
const PublicShopInfoPage = lazy(() => import("./pages/PublicShopInfoPage"));
const PublicContactPage = lazy(() => import("./pages/PublicContactPage"));
const SalesPage = lazy(() => import("./pages/SalesPage"));
const CompaniesPage = lazy(() => import("./pages/CompaniesPage"));
const RecurringBookingsPage = lazy(() => import("./pages/RecurringBookingsPage"));
const TransactionsPage = lazy(() => import("./pages/TransactionsPage"));
const UserRolesPage = lazy(() => import("./pages/UserRolesPage"));
const LogsPage = lazy(() => import("./pages/LogsPage"));
const ChangePasswordPage = lazy(() => import("./pages/ChangePasswordPage"));
const AttendancePage = lazy(() => import("./pages/AttendancePage"));
const AdReportsPage = lazy(() => import("./pages/AdReportsPage"));
const ChangelogPage = lazy(() => import("./pages/ChangelogPage"));
const DebugReportsPage = lazy(() => import("./pages/DebugReportsPage"));
const OAuthRedirectPage = lazy(() => import("./pages/OAuthRedirectPage"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="flex-1 flex items-center justify-center">
    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ErrorBoundary>
          <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Public pages (no auth required) */}
            <Route
              path="/oauth-redirect"
              element={
                <Suspense fallback={<PageLoader />}>
                  <OAuthRedirectPage />
                </Suspense>
              }
            />
            <Route
              path="/book/:urlKey"
              element={
                <Suspense fallback={<PageLoader />}>
                  <PublicBookingPage />
                </Suspense>
              }
            />
            <Route
              path="/booking-check"
              element={
                <Suspense fallback={<PageLoader />}>
                  <PublicBookingCheckPage />
                </Suspense>
              }
            />
            <Route
              path="/menu-list/:urlKey"
              element={
                <Suspense fallback={<PageLoader />}>
                  <PublicMenuListPage />
                </Suspense>
              }
            />
            <Route
              path="/shop-info/:shopId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <PublicShopInfoPage />
                </Suspense>
              }
            />
            <Route
              path="/contact/:shopId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <PublicContactPage />
                </Suspense>
              }
            />
            <Route
              element={
                <AuthGuard>
                  <DashboardLayout />
                </AuthGuard>
              }
            >
              <Route
                path="/"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Index />
                  </Suspense>
                }
              />
              <Route
                path="/shifts"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ShiftsPage />
                  </Suspense>
                }
              />
              <Route
                path="/customers"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <CustomersPage />
                  </Suspense>
                }
              />
              <Route
                path="/reports"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ReportsPage />
                  </Suspense>
                }
              />
              <Route
                path="/booking-urls"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <BookingUrlsPage />
                  </Suspense>
                }
              />
              <Route
                path="/settings"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <SettingsPage />
                  </Suspense>
                }
              />
              <Route
                path="/shops"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ShopsPage />
                  </Suspense>
                }
              />
              <Route
                path="/staff"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <StaffPage />
                  </Suspense>
                }
              />
              <Route
                path="/sales"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <SalesPage />
                  </Suspense>
                }
              />
              <Route
                path="/companies"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <CompaniesPage />
                  </Suspense>
                }
              />
              <Route
                path="/integrations"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <IntegrationsPage />
                  </Suspense>
                }
              />
              <Route
                path="/recurring-bookings"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <RecurringBookingsPage />
                  </Suspense>
                }
              />
              <Route
                path="/transactions"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <TransactionsPage />
                  </Suspense>
                }
              />
              <Route
                path="/user-roles"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <UserRolesPage />
                  </Suspense>
                }
              />
              <Route
                path="/logs"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <LogsPage />
                  </Suspense>
                }
              />
              <Route
                path="/change-password"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ChangePasswordPage />
                  </Suspense>
                }
              />
              <Route
                path="/attendance"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <AttendancePage />
                  </Suspense>
                }
              />
              <Route
                path="/ad-reports"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <AdReportsPage />
                  </Suspense>
                }
              />
              <Route
                path="/changelog"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ChangelogPage />
                  </Suspense>
                }
              />
              <Route
                path="/debug-reports"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <DebugReportsPage />
                  </Suspense>
                }
              />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
          </AuthProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
